import { Injectable } from '@angular/core';
import { LinkDetails, LinkDto } from '@flyfreely-portal-ui/flyfreely';
import { Apollo, gql } from 'apollo-angular';
import { ILinksService } from 'libs/attachments/src/lib/link-manager/link-manager.interface';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class LotamLinkManagerService implements ILinksService {
    constructor(private apollo: Apollo) {}

    createLink(noticeId: number, details: LinkDetails): Observable<LinkDto> {
        return this.apollo
            .mutate<{ createNoticeLink: LinkDto }>({
                mutation: gql`
                    mutation createNoticeLink(
                        $noticeId: Long!
                        $details: LinkDetails!
                    ) {
                        createNoticeLink(
                            noticeId: $noticeId
                            details: $details
                        ) {
                            id
                            title
                            url
                        }
                    }
                `,
                variables: {
                    noticeId,
                    details
                }
            })
            .pipe(
                tap(() => this.loadLinks(noticeId)), // Reload links after creating
                map(response => response.data.createNoticeLink)
            );
    }

    updateLink(
        noticeId: number,
        linkId: number,
        details: LinkDetails
    ): Observable<LinkDto> {
        return this.apollo
            .mutate<{ updateNoticeLink: LinkDto }>({
                mutation: gql`
                    mutation updateNoticeLink(
                        $noticeId: Long!
                        $linkId: Long!
                        $details: LinkDetails!
                    ) {
                        updateNoticeLink(
                            noticeId: $noticeId
                            linkId: $linkId
                            details: $details
                        ) {
                            id
                            title
                            url
                        }
                    }
                `,
                variables: {
                    noticeId,
                    linkId,
                    details
                }
            })
            .pipe(
                tap(() => this.loadLinks(noticeId)), // Reload links after updating
                map(response => response.data.updateNoticeLink)
            );
    }

    deleteLink(noticeId: number, linkId: number): Observable<boolean> {
        return this.apollo
            .mutate<{ deleteNoticeLink: { success: boolean } }>({
                mutation: gql`
                    mutation deleteNoticeLink(
                        $noticeId: Long!
                        $linkId: Long!
                    ) {
                        deleteNoticeLink(noticeId: $noticeId, linkId: $linkId) {
                            success
                        }
                    }
                `,
                variables: {
                    noticeId,
                    linkId
                }
            })
            .pipe(
                tap(() => this.loadLinks(noticeId)), // Reload links after deleting
                map(response => response.data.deleteNoticeLink.success)
            );
    }

    loadLinks(noticeId: number): Observable<LinkDto[]> {
        return this.apollo
            .query<{
                findNoticeLinks: LinkDto[];
            }>({
                query: gql`
                    query findNoticeLinks($noticeId: Long!) {
                        findNoticeLinks(noticeId: $noticeId) {
                            id
                            title
                            url
                        }
                    }
                `,
                variables: {
                    noticeId
                },
                fetchPolicy: 'network-only'
            })
            .pipe(map(response => response.data.findNoticeLinks));
    }
}
